import type { ComputedRef, UnwrapNestedRefs } from '@vue/composition-api';
import { computed, reactive } from '@vue/composition-api';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type {
  Chat,
  ChatParticipant,
  Conversation,
  LastReadBy,
  Message,
  MessageState,
  ReadBy,
} from '../types';
import { ConversationType } from '../types';
import { ChatHelper } from '../helpers/chat.helper';

export class BaseChatCompositionApi {
  static setDefaultConversation(chat: UnwrapNestedRefs<Chat>): void {
    chat.conversation = chat.conversations.find(
      (conversation: Conversation) => conversation.type === ConversationType.General
    );
  }

  static initChat(): UnwrapNestedRefs<Chat> {
    return reactive<Chat>({
      conversation: undefined,
      conversations: [],
      typing: [],
      error: false,
      loadingConversationList: true,
      loadingConversationDetails: true,
      replyMessage: undefined,
    });
  }

  static addMessage(
    message: Message,
    id: string,
    chat: UnwrapNestedRefs<Chat>,
    replace?: boolean
  ): void {
    const messageExists = !!chat.conversation.messages.find((val: Message) => val.id === id);
    const newMessages: Message[] = chat.conversation.messages.map((val: Message) => {
      if (val.id === id && replace) {
        return message;
      }
      return val;
    });
    chat.conversation.messages = messageExists ? newMessages : [...newMessages, message];
  }

  static useMapChatParticipant(
    participant: ChatParticipant,
    recipients: Recipient[],
    user: User
  ): ComputedRef<ChatParticipant> {
    return computed(() => this.mapChatParticipant(participant, recipients, user));
  }

  protected static mapChatParticipant(
    participant: ChatParticipant,
    recipients: Recipient[],
    user: User
  ): ChatParticipant {
    if (participant.recipientId) {
      return {
        ...participant,
        recipient: recipients.find(
          (recipient: Recipient) => recipient.id === participant.recipientId
        ),
      };
    }
    return {
      ...participant,
      user,
    };
  }

  static useConversationLastReadList(
    chat: UnwrapNestedRefs<Chat>,
    recipients: Recipient[],
    user: User,
    owner: ChatParticipant
  ): ComputedRef<LastReadBy[]> {
    return computed(() => {
      const reads: LastReadBy[] = this.findLastReadList(chat, recipients, user).filter(
        (read: LastReadBy) => !!read
      );
      return reads
        .filter(
          ({ readBy: { participant } }: LastReadBy) =>
            !ChatHelper.isSameParticipant(participant, owner)
        )
        .map(({ readBy, messageId }: LastReadBy) => ({
          messageId,
          readBy: {
            participant: this.mapChatParticipant(readBy.participant, recipients, user),
            timestamp: readBy.timestamp,
          },
        }));
    });
  }

  protected static findLastReadList(
    chat: UnwrapNestedRefs<Chat>,
    recipients: Recipient[],
    user: User
  ): LastReadBy[] {
    if (chat.conversation.type === ConversationType.Private) {
      return chat.conversation.participants.map(({ recipientId, userId }: ChatParticipant) =>
        this.findLastRead(chat.conversation.messages, recipientId, userId)
      );
    }
    const recipientLastReadBy: {
      readBy: ReadBy;
      messageId: string;
    }[] = recipients.map((recipient: Recipient) =>
      this.findLastRead(chat.conversation.messages, recipient.id)
    );
    const userLastReadBy: LastReadBy = this.findLastRead(
      chat.conversation.messages,
      undefined,
      user.id
    );
    return [...recipientLastReadBy, userLastReadBy];
  }

  protected static findLastRead(
    messages: Message[],
    recipientId?: string,
    userId?: string
  ): { messageId: string; readBy: ReadBy } {
    for (const message of [...messages].reverse()) {
      const readBy: ReadBy = message.reads.find(({ participant }: ReadBy) => {
        if (recipientId && participant.recipientId) {
          return recipientId === participant.recipientId;
        }
        return userId === participant.userId;
      });
      if (readBy) {
        return {
          messageId: message.id,
          readBy,
        };
      }
    }
  }

  static getMessageState(
    message: Message,
    user: User,
    recipients: Recipient[],
    messages: Message[],
    owner: ChatParticipant
  ): UnwrapNestedRefs<MessageState> {
    const name: string = ChatHelper.getFullname(message, user, recipients);
    const thumbUrl: string = ChatHelper.getThumbUrl(message, user, recipients);
    const date: string = ChatHelper.date(message.createdAt);
    const time: string = ChatHelper.time(message.createdAt);
    const fullTimestamp: string = ChatHelper.fullTimestamp(message.createdAt);
    const isBySender: boolean = ChatHelper.isSameParticipant(message.owner, owner);
    const nextMessage: Message = ChatHelper.getNextMessage(message, messages);
    const previousMessage: Message = ChatHelper.getPreviousMessage(message, messages);
    const lastMessage: Message = ChatHelper.getLastMessage(messages);
    const isFirstMessageInBlock: boolean = ChatHelper.isFirstMessageInBlock(
      previousMessage,
      message
    );
    const showTimestamp: boolean = ChatHelper.showTimestamp(previousMessage, message.createdAt);
    const isLastMessageInBlock: boolean = ChatHelper.isLastMessageInBlock(
      message,
      lastMessage.id,
      nextMessage
    );
    const replyMessage: Message = ChatHelper.getReplyMessage(message, messages);

    return reactive({
      name,
      thumbUrl,
      date,
      time,
      fullTimestamp,
      isBySender,
      showTimestamp,
      isFirstMessageInBlock,
      isLastMessageInBlock,
      replyMessage,
    });
  }
}
